import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, BrowserRouter } from "react-router-dom";
import { toast, ToastContainer, Slide } from "react-toastify";
import { AppProvider, Banner, Page } from "@shopify/polaris";
import translations from "@shopify/polaris/locales/en.json";
import "@shopify/polaris/build/esm/styles.css";
import "react-toastify/dist/ReactToastify.css";
import Auth from "./Auth";
import "./App.css";
import Home from "./Home";
import CenteredSpinner from "./components/CenteredSpinner";
import { redirectToOauthFlow } from "./utils/navUtils";
import { createSession } from "./utils/session";

const BLOCKED_SITE_DATA_ERROR = "Failed to read the 'localStorage' property";
const SESSION_ERROR =
  "We couldn't connect to Postscript. Please reload the page, or contact support if this error persists.";
const { search } = window.location;
const params = new URLSearchParams(search);
const host = params.get("host");
const shop = params.get("shop");
export const shopifyConfig = {
  apiKey: process.env.REACT_APP_SHOPIFY_API_KEY,
  host,
  shop,
};

const App = ({ sessionReady }) => (
  <AppProvider i18n={translations}>
    <Routes>
      <Route exact path="/" element={<Home sessionReady={sessionReady} />} />
      <Route exact path="/auth" element={<Auth />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
    <ToastContainer
      hideProgressBar
      limit={5}
      position="bottom-center"
      theme="light"
      transition={Slide}
    />
  </AppProvider>
);

const AppWrapper = () => {
  const params = new URLSearchParams(window.location.search);
  const shop = params.get("shop");
  const [loading, setLoading] = useState(true);
  const [sessionReady, setSessionReady] = useState(false);
  const [sessionError, setSessionError] = useState(false);

  if (!shop) {
    // TODO: Replace with something better (like a login screen, or just some text instructions)
    return;
  }
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        // Top-level install flow: there's no admin host to answer idToken(), so go straight to OAuth
        if (!window.shopify?.environment?.embedded) {
          await redirectToOauthFlow(false);
          return;
        }

        const session = await createSession();

        if (session.status === 404 || session.uninstalled) {
          await redirectToOauthFlow(false);
          return;
        }

        if (!session.ok || !session.access_token) {
          setSessionError(true);
          toast.error(SESSION_ERROR);
          return;
        }

        setSessionReady(true);
      } catch (exception) {
        if (exception.toString().includes(BLOCKED_SITE_DATA_ERROR)) {
          toast.error(
            "3rd party data and cookies are currently blocked. To get full access to the Postscript Sales Channel App, please enable them in your browser settings."
          );
        } else {
          console.error(exception);
          setSessionError(true);
          toast.error(SESSION_ERROR);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <CenteredSpinner />;
  }

  if (sessionError) {
    return (
      <AppProvider i18n={translations}>
        <Page>
          <Banner status="critical">{SESSION_ERROR}</Banner>
        </Page>
        <ToastContainer
          hideProgressBar
          position="bottom-center"
          theme="light"
        />
      </AppProvider>
    );
  }

  return (
    <BrowserRouter>
      <App sessionReady={sessionReady} />
    </BrowserRouter>
  );
};

export default AppWrapper;
